import { resolveItems } from "../catalog/catalogModel";
import { toResolveContext } from "../catalog/context";
import type { CatalogContextInput } from "../catalog/context";
import type { CatalogItemsFilterConfig } from "../catalog/posCard";
import { DEFAULT_RECOMMENDATION_LIMIT, RECOMMENDATION_CATALOG_FILTER } from "./constants";
import type {
  RecommendationInfo,
  RecommendationItemMeta,
  RecommendationsResponse,
  RecommendedItem,
} from "./types";

/**
 * `recommendations/selection` — from an engine response to the list a surface renders.
 *
 * The pipeline, in order:
 *
 *   1. drop items already in the cart          — suggesting what the guest just added is noise
 *   2. drop items from an already-taken category — one dessert suggestion, not three
 *   3. project through the catalogue           — resolve variants, price, schedule, sold-out, channel
 *   4. cap to the surface's limit
 *
 * Steps 1, 2 and 4 are pure set logic and live here unconditionally. Step 3 is delegated to
 * `catalog`, which is equally store-free — so the WHOLE pipeline can be exercised in
 * a test with plain objects, and a surface with different rules (a kiosk showing six suggestions
 * with no category rule) changes arguments rather than forking the code.
 */

/** `itemId -> the engine's metadata for it`. `{}` when the engine sent no `itemList`. */
export function buildItemMetaMap(
  recommendations: RecommendationsResponse | undefined,
): Record<string, RecommendationItemMeta> {
  const itemList = recommendations?.itemList;
  if (!itemList?.length) return {};
  return itemList.reduce<Record<string, RecommendationItemMeta>>((acc, meta) => {
    if (meta?.itemId) {
      acc[meta.itemId] = meta;
    }
    return acc;
  }, {});
}

/** The model's category for an item, or `undefined` when the engine did not classify it. */
export function getClassificationCategory(
  metaMap: Record<string, RecommendationItemMeta>,
  itemId: string,
): string | undefined {
  return metaMap?.[itemId]?.metadata?.classification_category;
}

/**
 * Steps 1 and 2: the engine's ranked ids, minus what the cart already holds and minus anything
 * from a category the guest has already taken a suggestion from.
 *
 * Ranking is preserved — this only removes.
 */
export function selectEligibleItemIds(input: {
  recommendations: RecommendationsResponse | undefined;
  /** Item ids currently in the cart. */
  cartItemIds?: ReadonlySet<string>;
  /** Classification categories already accepted this recommendation round. */
  excludedCategories?: ReadonlySet<string>;
  metaMap?: Record<string, RecommendationItemMeta>;
}): string[] {
  const { recommendations, cartItemIds, excludedCategories } = input;
  const items = recommendations?.items;
  if (!items?.length) return [];

  const metaMap = input.metaMap ?? buildItemMetaMap(recommendations);

  return items.filter((itemMid) => {
    if (cartItemIds?.has(itemMid)) return false;
    if (excludedCategories?.size) {
      const category = getClassificationCategory(metaMap, itemMid);
      if (category && excludedCategories.has(category)) return false;
    }
    return true;
  });
}

/**
 * Steps 3 and 4: project the eligible ids through the catalogue and cap the result.
 *
 * `recommendationInfo` is attached HERE rather than at the click handler so that a surface cannot
 * forget it — attribution is what the whole funnel is measured on.
 */
export function projectRecommendedItems(input: {
  itemIds: string[];
  catalogContext: CatalogContextInput;
  recommendationInfo: RecommendationInfo;
  metaMap?: Record<string, RecommendationItemMeta>;
  limit?: number;
  catalogFilter?: CatalogItemsFilterConfig;
}): RecommendedItem[] {
  const {
    itemIds,
    catalogContext,
    recommendationInfo,
    metaMap,
    limit = DEFAULT_RECOMMENDATION_LIMIT,
    catalogFilter = RECOMMENDATION_CATALOG_FILTER,
  } = input;

  if (!itemIds?.length) return [];

  /*
    🔴 The LEAN card (`toCatalogItem`), not `getCatalogItemsFromIds`'s fat POS one.

    Same pipeline, same filters, same `sort: "input"` — only the projection at the end differs.
    Three things follow, and all three are why this changed:

      · the kiosk stops narrowing the result into a third card type of its own;
      · `name` comes from `displayNameOf`, so an item with only a `shortName` is no longer blank;
      · `validPrice` becomes LIVE. The fat builder coerces a `NaN` price to `0` before the filter
        can see it, so `RECOMMENDATION_CATALOG_FILTER.validPrice` did nothing. An unpriced
        suggestion is now dropped — and since the cap is applied AFTER projection, that returns a
        slot to a suggestion the guest can actually buy rather than spending it on a zero-priced
        card.

    `sort: "input"` is REQUIRED, not incidental: the engine returns ids best-first and the cap
    below is applied after this, so menu order would change WHICH items survive it.
  */
  const catalogItems = resolveItems(itemIds, toResolveContext(catalogContext), {
    ...catalogFilter,
    sort: "input",
  });

  return catalogItems.slice(0, limit).map((item) => ({
    ...item,
    classificationCategory: metaMap ? getClassificationCategory(metaMap, item.id) : undefined,
    recommendationInfo,
  }));
}

/**
 * The whole pipeline in one call — what every surface actually wants.
 *
 * ⚠️ The cap is applied AFTER the catalogue projection, not before: an id that the catalogue
 * rejects (sold out, off-channel, out of schedule) must not consume one of the slots, or the
 * widget shows two suggestions when it could have shown three.
 */
export function selectRecommendedItems(input: {
  recommendations: RecommendationsResponse | undefined;
  catalogContext: CatalogContextInput;
  recommendationProvider?: string;
  cartItemIds?: ReadonlySet<string>;
  excludedCategories?: ReadonlySet<string>;
  limit?: number;
  catalogFilter?: CatalogItemsFilterConfig;
}): RecommendedItem[] {
  const {
    recommendations,
    catalogContext,
    recommendationProvider,
    cartItemIds,
    excludedCategories,
    limit,
    catalogFilter,
  } = input;

  const metaMap = buildItemMetaMap(recommendations);
  const eligibleIds = selectEligibleItemIds({
    recommendations,
    cartItemIds,
    excludedCategories,
    metaMap,
  });

  return projectRecommendedItems({
    itemIds: eligibleIds,
    catalogContext,
    metaMap,
    limit,
    catalogFilter,
    recommendationInfo: {
      recommendationId: recommendations?.recommendationId,
      recommendationProvider,
    },
  });
}
